/**
 * SystemMetricsCards Enterprise v4.0 - Advanced System Metrics Display
 * Visualizzazione real-time delle metriche di sistema con design shadcn/ui professionale
 * Responsive, animato e enterprise-grade
 */

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMetrics } from "@/hooks/useMetrics";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import {
    Cpu,
    MemoryStick,
    HardDrive,
    Network,
    TrendingUp,
    TrendingDown,
    Activity,
    Zap,
} from "lucide-react";

interface SystemMetricsCardsProps {
    metrics?: {
        cpu: number | null;
        memory: number | null;
        disk: number | null;
        network: number | null;
    };
    className?: string;
}

type MetricColor = "blue" | "green" | "purple" | "orange";
type MetricTrend = "up" | "down" | "stable" | "unknown";
type MetricStatus = "good" | "warning" | "critical" | "unknown";

const COLOR: Record<
    MetricColor,
    {
        iconBg: string;
        iconBgHover: string;
        iconText: string;
        gradient: string;
        glow: string;
    }
> = {
    blue: {
        iconBg: "bg-blue-500/10",
        iconBgHover: "group-hover:bg-blue-500/20",
        iconText: "text-blue-500",
        gradient: "from-blue-500 to-blue-400",
        glow: "from-blue-500/5",
    },
    green: {
        iconBg: "bg-green-500/10",
        iconBgHover: "group-hover:bg-green-500/20",
        iconText: "text-green-500",
        gradient: "from-green-500 to-green-400",
        glow: "from-green-500/5",
    },
    purple: {
        iconBg: "bg-purple-500/10",
        iconBgHover: "group-hover:bg-purple-500/20",
        iconText: "text-purple-500",
        gradient: "from-purple-500 to-purple-400",
        glow: "from-purple-500/5",
    },
    orange: {
        iconBg: "bg-orange-500/10",
        iconBgHover: "group-hover:bg-orange-500/20",
        iconText: "text-orange-500",
        gradient: "from-orange-500 to-orange-400",
        glow: "from-orange-500/5",
    },
};

export const SystemMetricsCards: React.FC<SystemMetricsCardsProps> = ({
    metrics: propMetrics,
    className = "",
}) => {
    // Prefer hook metrics; fallback to props.
    const hookData = useMetrics();
    const { metrics: hookMetrics, isLoading } = hookData || {};

    const numOrNull = (v: unknown): number | null => {
        if (typeof v !== "number") return null;
        return Number.isFinite(v) ? v : null;
    };
    const metrics = hookMetrics
        ? {
              cpu: numOrNull(hookMetrics.system.cpu_percent),
              memory: numOrNull(hookMetrics.system.memory_percent),
              disk: numOrNull(hookMetrics.system.disk_percent),
              network: (() => {
                  const io = hookMetrics.system.network_io;
                  const sent = numOrNull((io as any)?.bytes_sent);
                  const recv = numOrNull((io as any)?.bytes_recv);
                  if (sent === null || recv === null) return null;
                  return (sent + recv) / 1024 / 1024;
              })(),
          }
        : propMetrics || { cpu: null, memory: null, disk: null, network: null };

    if (isLoading) {
        return (
            <div
                className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 ${className}`}
            >
                {[...Array(4)].map((_, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: i * 0.1 }}
                    >
                        <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <div className="h-4 bg-muted rounded-lg w-24 animate-pulse" />
                                    <div className="h-8 w-8 bg-muted rounded-lg animate-pulse" />
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="h-8 bg-muted rounded-lg w-16 animate-pulse" />
                                <div className="h-2 bg-muted rounded-full w-full animate-pulse" />
                                <div className="flex justify-between">
                                    <div className="h-3 bg-muted rounded w-20 animate-pulse" />
                                    <div className="h-5 bg-muted rounded-full w-12 animate-pulse" />
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                ))}
            </div>
        );
    }

    // Create system metrics array
    const systemMetrics: Array<{
        label: string;
        value: number | null;
        unit: string;
        icon: any;
        color: MetricColor;
        trend: MetricTrend;
        status: MetricStatus;
    }> = [
        {
            label: "Uso CPU",
            value: metrics.cpu,
            unit: "%",
            icon: Cpu,
            color: "blue",
            trend:
                typeof metrics.cpu === "number" && metrics.cpu > 80
                    ? "up"
                    : typeof metrics.cpu === "number" && metrics.cpu < 30
                      ? "down"
                      : typeof metrics.cpu === "number"
                        ? "stable"
                        : "unknown",
            status:
                typeof metrics.cpu === "number" && metrics.cpu > 90
                    ? "critical"
                    : typeof metrics.cpu === "number" && metrics.cpu > 70
                      ? "warning"
                      : typeof metrics.cpu === "number"
                        ? "good"
                        : "unknown",
        },
        {
            label: "Memoria",
            value: metrics.memory,
            unit: "%",
            icon: MemoryStick,
            color: "green",
            trend:
                typeof metrics.memory === "number" && metrics.memory > 85
                    ? "up"
                    : typeof metrics.memory === "number" && metrics.memory < 40
                      ? "down"
                      : typeof metrics.memory === "number"
                        ? "stable"
                        : "unknown",
            status:
                typeof metrics.memory === "number" && metrics.memory > 95
                    ? "critical"
                    : typeof metrics.memory === "number" && metrics.memory > 80
                      ? "warning"
                      : typeof metrics.memory === "number"
                        ? "good"
                        : "unknown",
        },
        {
            label: "Uso disco",
            value: metrics.disk,
            unit: "%",
            icon: HardDrive,
            color: "purple",
            trend:
                typeof metrics.disk === "number"
                    ? metrics.disk > 90
                        ? "up"
                        : "stable"
                    : "unknown",
            status:
                typeof metrics.disk === "number" && metrics.disk > 95
                    ? "critical"
                    : typeof metrics.disk === "number" && metrics.disk > 85
                      ? "warning"
                      : typeof metrics.disk === "number"
                        ? "good"
                        : "unknown",
        },
        {
            label: "Traffico rete",
            value: metrics.network,
            unit: "MB",
            icon: Network,
            color: "orange",
            trend:
                typeof metrics.network === "number" && metrics.network > 100
                    ? "up"
                    : typeof metrics.network === "number" && metrics.network < 10
                      ? "down"
                      : typeof metrics.network === "number"
                        ? "stable"
                        : "unknown",
            status: typeof metrics.network === "number" ? "good" : "unknown",
        },
    ];

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "good":
                return "default";
            case "warning":
                return "secondary";
            case "critical":
                return "destructive";
            case "unknown":
                return "outline";
            default:
                return "outline";
        }
    };

    const getTrendIcon = (trend: string) => {
        switch (trend) {
            case "up":
                return TrendingUp;
            case "down":
                return TrendingDown;
            case "unknown":
                return Activity;
            default:
                return Activity;
        }
    };

    const getTrendColor = (trend: string) => {
        switch (trend) {
            case "up":
                return "text-red-500";
            case "down":
                return "text-green-500";
            case "unknown":
                return "text-muted-foreground";
            default:
                return "text-muted-foreground";
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case "good":
                return "ok";
            case "warning":
                return "attenzione";
            case "critical":
                return "critico";
            default:
                return "sconosciuto";
        }
    };

    return (
        <div
            className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 ${className}`}
        >
            <AnimatePresence>
                {systemMetrics.map((metric, index) => {
                    const Icon = metric.icon;
                    const TrendIcon = getTrendIcon(metric.trend);
                    const color = COLOR[metric.color];
                    const valueText =
                        typeof metric.value === "number"
                            ? metric.value.toFixed(1)
                            : "n/a";
                    const progress =
                        typeof metric.value === "number"
                            ? Math.min(metric.value, 100)
                            : 0;

                    return (
                        <motion.div
                            key={metric.label}
                            initial={{ opacity: 0, y: 20, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -20, scale: 0.9 }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                            whileHover={{ y: -4, scale: 1.02 }}
                            className="group"
                        >
                            <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-card to-card/50 hover:shadow-xl transition-all duration-300">
                                <CardHeader className="pb-3">
                                        <CardTitle className="text-sm font-medium flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div
                                                    className={`p-2.5 rounded-xl ${color.iconBg} ${color.iconBgHover} transition-colors`}
                                                >
                                                    <Icon
                                                        className={`h-4 w-4 ${color.iconText}`}
                                                    />
                                                </div>
                                                <span className="group-hover:text-primary transition-colors">
                                                    {metric.label}
                                            </span>
                                        </div>
                                        <Badge
                                            variant={getStatusBadge(
                                                metric.status,
                                            )}
                                            className="text-xs capitalize"
                                            >
                                                {getStatusText(metric.status)}
                                            </Badge>
                                        </CardTitle>
                                    </CardHeader>

                                <CardContent className="space-y-4">
                                    {/* Value Display */}
                                    <div className="flex items-end gap-2">
                                        <motion.span
                                            className="text-3xl font-bold text-foreground"
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{
                                                duration: 0.5,
                                                delay: index * 0.1 + 0.2,
                                            }}
                                            >
                                                {valueText}
                                            </motion.span>
                                            <span className="text-sm text-muted-foreground mb-1 font-medium">
                                                {metric.unit}
                                            </span>
                                        </div>

                                    {/* Progress Bar */}
                                    <div className="space-y-2">
                                        <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                                                <motion.div
                                                    className={`h-full rounded-full transition-all duration-700 ${
                                                        metric.status === "critical"
                                                            ? "bg-gradient-to-r from-red-500 to-red-400"
                                                            : metric.status === "warning"
                                                              ? "bg-gradient-to-r from-yellow-500 to-yellow-400"
                                                              : metric.status === "unknown"
                                                                ? "bg-gradient-to-r from-muted-foreground/40 to-muted-foreground/20"
                                                                : `bg-gradient-to-r ${color.gradient}`
                                                    }`}
                                                    initial={{ width: 0 }}
                                                    animate={{
                                                        width: `${progress}%`,
                                                    }}
                                                    transition={{
                                                        duration: 1,
                                                        delay: index * 0.2 + 0.3,
                                                    }}
                                            />
                                        </div>
                                    </div>

                                    {/* Trend and Actions */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1.5 text-xs">
                                            <TrendIcon
                                                className={`h-3 w-3 ${getTrendColor(metric.trend)}`}
                                            />
                                            <span
                                                    className={`${getTrendColor(metric.trend)} font-medium`}
                                                >
                                                    {metric.trend === "up"
                                                        ? "In aumento"
                                                        : metric.trend === "down"
                                                          ? "In calo"
                                                          : metric.trend === "unknown"
                                                            ? "Sconosciuto"
                                                            : "Stabile"}
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-1">
                                                <Zap className="h-3 w-3 text-primary animate-pulse" />
                                            </div>
                                        </div>
                                    </CardContent>

                                {/* Glow Effect */}
                                    <div
                                        className={`absolute inset-0 bg-gradient-to-r ${color.glow} to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
                                    />

                                {/* Status Indicator */}
                                <div
                                    className={`absolute top-2 right-2 w-2 h-2 rounded-full ${
                                        metric.status === "critical"
                                            ? "bg-red-500"
                                            : metric.status === "warning"
                                              ? "bg-yellow-500"
                                              : "bg-green-500"
                                    } animate-pulse`}
                                />
                            </Card>
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </div>
    );
};
